/**
 * Session Manager
 *
 * Управляет жизненным циклом sessionId с TTL 7 дней.
 * SessionId сохраняется в localStorage и восстанавливается при перезагрузке страницы.
 */

// Константы
const SESSION_TTL = 7 * 24 * 60 * 60 * 1000; // 7 дней в миллисекундах
const STORAGE_KEY_SESSION_ID = 'app_sessionId';
const STORAGE_KEY_SESSION_TIMESTAMP = 'app_sessionTimestamp';
const STORAGE_KEY_VISITOR_ID = 'sg_visitorId';

/**
 * Генерация нового UUID v4
 * @returns {string} UUID
 */
function generateUUID() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }

  // Fallback для старых браузеров
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
}

/**
 * Получить или создать sessionId
 *
 * Проверяет наличие существующего sessionId в localStorage.
 * Если sessionId существует и не истёк (< 7 дней) - возвращает его.
 * Если истёк или отсутствует - генерирует новый и сохраняет с текущим timestamp.
 *
 * @returns {string} SessionId (UUID v4)
 */
export function getOrCreateSessionId() {
  try {
    const existingSessionId = localStorage.getItem(STORAGE_KEY_SESSION_ID);
    const existingTimestamp = localStorage.getItem(STORAGE_KEY_SESSION_TIMESTAMP);

    if (existingSessionId && existingTimestamp) {
      const timestamp = parseInt(existingTimestamp, 10);
      const now = Date.now();
      const age = now - timestamp;

      // Проверяем, не истёк ли sessionId
      if (age < SESSION_TTL) {
        console.log(
          `[SessionManager] Using existing sessionId: ${existingSessionId} ` +
          `(age: ${Math.round(age / 1000 / 60)} minutes, TTL: ${SESSION_TTL / 1000 / 60} minutes)`
        );
        return existingSessionId;
      } else {
        console.log(
          `[SessionManager] SessionId expired (age: ${Math.round(age / 1000 / 60)} minutes), ` +
          'generating new one'
        );
      }
    }

    // Генерируем новый sessionId
    const newSessionId = generateUUID();
    const now = Date.now();

    localStorage.setItem(STORAGE_KEY_SESSION_ID, newSessionId);
    localStorage.setItem(STORAGE_KEY_SESSION_TIMESTAMP, now.toString());

    console.log(`[SessionManager] Created new sessionId: ${newSessionId}`);

    return newSessionId;
  } catch (error) {
    console.error('[SessionManager] Error accessing localStorage:', error);

    // Fallback: генерируем sessionId без сохранения
    return generateUUID();
  }
}

/**
 * Обновить timestamp сессии
 *
 * Продлевает жизнь текущей сессии, обновляя timestamp до текущего времени.
 * Используется при активных действиях пользователя для продления сессии.
 *
 * @returns {boolean} true если успешно обновлено, false в случае ошибки
 */
export function refreshSession() {
  try {
    const existingSessionId = localStorage.getItem(STORAGE_KEY_SESSION_ID);

    if (!existingSessionId) {
      console.warn('[SessionManager] Cannot refresh session: no sessionId found');
      return false;
    }

    const now = Date.now();
    localStorage.setItem(STORAGE_KEY_SESSION_TIMESTAMP, now.toString());

    console.log(`[SessionManager] Session refreshed for sessionId: ${existingSessionId}`);
    return true;
  } catch (error) {
    console.error('[SessionManager] Error refreshing session:', error);
    return false;
  }
}

/**
 * Очистить текущую сессию
 *
 * Принудительно удаляет sessionId и timestamp из localStorage.
 * Используется при выходе пользователя (logout) или для сброса контекста.
 *
 * @returns {boolean} true если успешно очищено, false в случае ошибки
 */
export function clearSession() {
  try {
    const existingSessionId = localStorage.getItem(STORAGE_KEY_SESSION_ID);

    localStorage.removeItem(STORAGE_KEY_SESSION_ID);
    localStorage.removeItem(STORAGE_KEY_SESSION_TIMESTAMP);

    console.log(`[SessionManager] Session cleared: ${existingSessionId || 'no session'}`);
    return true;
  } catch (error) {
    console.error('[SessionManager] Error clearing session:', error);
    return false;
  }
}

/**
 * Получить текущий sessionId без создания нового
 *
 * Возвращает существующий sessionId или null если его нет.
 * Не проверяет TTL и не создаёт новый sessionId.
 *
 * @returns {string|null} SessionId или null
 */
export function getCurrentSessionId() {
  try {
    return localStorage.getItem(STORAGE_KEY_SESSION_ID);
  } catch (error) {
    console.error('[SessionManager] Error getting current sessionId:', error);
    return null;
  }
}

/**
 * Получить информацию о текущей сессии
 *
 * @returns {Object} Информация о сессии
 */
export function getSessionInfo() {
  try {
    const sessionId = localStorage.getItem(STORAGE_KEY_SESSION_ID);
    const timestampStr = localStorage.getItem(STORAGE_KEY_SESSION_TIMESTAMP);

    if (!sessionId || !timestampStr) {
      return {
        exists: false,
        sessionId: null,
        timestamp: null,
        age: null,
        ttl: SESSION_TTL,
        expired: true,
      };
    }

    const timestamp = parseInt(timestampStr, 10);
    const now = Date.now();
    const age = now - timestamp;
    const expired = age >= SESSION_TTL;

    return {
      exists: true,
      sessionId,
      timestamp,
      age,
      ageMinutes: Math.round(age / 1000 / 60),
      ttl: SESSION_TTL,
      ttlMinutes: SESSION_TTL / 1000 / 60,
      expired,
      remainingTime: expired ? 0 : SESSION_TTL - age,
      remainingMinutes: expired ? 0 : Math.round((SESSION_TTL - age) / 1000 / 60),
    };
  } catch (error) {
    console.error('[SessionManager] Error getting session info:', error);
    return {
      exists: false,
      sessionId: null,
      timestamp: null,
      age: null,
      ttl: SESSION_TTL,
      expired: true,
      error: error.message,
    };
  }
}

/**
 * Проверить, истекла ли текущая сессия
 *
 * @returns {boolean} true если сессия истекла или отсутствует
 */
export function isSessionExpired() {
  const info = getSessionInfo();
  return info.expired;
}

/**
 * Browsing-session id — a REAL one (#1467 / #1400 A1).
 *
 * `app_sessionId` above is the DIALOG session: it is minted only when the chat
 * widget actually starts a conversation, and it lives for 7 days. That makes it
 * useless as a funnel denominator — measured on Andys, 54% of visitors never
 * have one at all, so any "per session" rate is computed over people who had
 * already interacted within the last week.
 *
 * This is the missing unit: created for EVERY visitor at beacon init, rolling
 * 30-minute idle window (the GA convention, so numbers are comparable to what
 * anyone expects a "session" to mean). localStorage rather than sessionStorage
 * so one visit spanning several tabs stays one session.
 */
const BROWSING_SESSION_TTL = 30 * 60 * 1000; // 30 minutes idle
const STORAGE_KEY_BROWSING_SESSION_ID = 'sg_browsingSessionId';
const STORAGE_KEY_BROWSING_SESSION_TS = 'sg_browsingSessionTs';

/**
 * In-memory fallback for when localStorage is unavailable.
 *
 * This MUST be module-scoped. The beacon stamps the browsing-session id onto
 * every event, so a fallback that minted a fresh UUID per call would give a
 * Safari-private-mode or partitioned-iframe visitor one "session" per event —
 * inflating the exact denominator this id exists to make trustworthy.
 */
let fallbackBrowsingSessionId = null;
let fallbackBrowsingSessionTs = 0;

export function getOrCreateBrowsingSessionId() {
  const now = Date.now();
  try {
    const existing = localStorage.getItem(STORAGE_KEY_BROWSING_SESSION_ID);
    const ts = parseInt(localStorage.getItem(STORAGE_KEY_BROWSING_SESSION_TS) || '0', 10);

    if (existing && ts && now - ts < BROWSING_SESSION_TTL) {
      // Slide the idle window forward on every event.
      localStorage.setItem(STORAGE_KEY_BROWSING_SESSION_TS, now.toString());
      return existing;
    }

    const fresh = generateUUID();
    localStorage.setItem(STORAGE_KEY_BROWSING_SESSION_ID, fresh);
    localStorage.setItem(STORAGE_KEY_BROWSING_SESSION_TS, now.toString());
    return fresh;
  } catch (error) {
    // Blocked storage. Keep the id stable in memory for the life of the page
    // and apply the same idle window, so the session still means one visit.
    console.error('[SessionManager] Error accessing localStorage for browsingSessionId:', error);
    if (
      !fallbackBrowsingSessionId ||
      now - fallbackBrowsingSessionTs >= BROWSING_SESSION_TTL
    ) {
      fallbackBrowsingSessionId = generateUUID();
    }
    fallbackBrowsingSessionTs = now;
    return fallbackBrowsingSessionId;
  }
}

/**
 * Получить или создать visitorId
 *
 * Persistent идентификатор посетителя без TTL — живёт в localStorage
 * до тех пор, пока пользователь не очистит storage.
 * Используется для трекинга уникальных посетителей across sessions.
 *
 * @returns {string} VisitorId (UUID v4)
 */

export function getOrCreateVisitorId() {
  try {
    const existingVisitorId = localStorage.getItem(STORAGE_KEY_VISITOR_ID);

    if (existingVisitorId) {
      return existingVisitorId;
    }

    const newVisitorId = generateUUID();
    localStorage.setItem(STORAGE_KEY_VISITOR_ID, newVisitorId);

    console.log(`[SessionManager] Created new visitorId: ${newVisitorId}`);
    return newVisitorId;
  } catch (error) {
    console.error('[SessionManager] Error accessing localStorage for visitorId:', error);
    return generateUUID();
  }
}

/**
 * Получить текущий visitorId без создания нового
 *
 * @returns {string|null} VisitorId или null
 */
export function getCurrentVisitorId() {
  try {
    return localStorage.getItem(STORAGE_KEY_VISITOR_ID);
  } catch (error) {
    console.error('[SessionManager] Error getting visitorId:', error);
    return null;
  }
}

// Экспортируем константы для использования в других модулях
export const SESSION_CONFIG = {
  TTL: SESSION_TTL,
  STORAGE_KEY_SESSION_ID,
  STORAGE_KEY_SESSION_TIMESTAMP,
  STORAGE_KEY_VISITOR_ID,
};

/**
 * Issue #494: Восстановить историю чата через REST API
 *
 * Используется для восстановления контекста чата после перезагрузки страницы
 * в режиме Website Navigation (chat-only виджет на сайте клиента).
 *
 * @param {string} apiUrl - Base URL API (например, 'https://app.shiftgears.ai/api')
 * @param {number} organizationId - ID организации
 * @returns {Promise<Object>} Объект с messages[] или пустой объект при ошибке
 */
export async function fetchChatHistory(apiUrl, organizationId) {
  try {
    const sessionId = getCurrentSessionId();

    if (!sessionId) {
      console.log('[SessionManager] No sessionId found, skipping chat history fetch');
      return { messages: [], totalCount: 0 };
    }

    // Удаляем trailing /api если есть (endpoint добавит свой путь)
    const baseUrl = apiUrl.replace(/\/api\/?$/, '');
    const url = `${baseUrl}/api/chat/history/${sessionId}?organizationId=${organizationId}`;

    console.log(`[SessionManager] Fetching chat history from ${url}`);

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      console.warn(`[SessionManager] Chat history fetch failed: ${response.status}`);
      return { messages: [], totalCount: 0 };
    }

    const data = await response.json();

    console.log(
      `[SessionManager] Chat history fetched: ${data.messages?.length || 0} messages, ` +
      `total: ${data.totalCount || 0}`
    );

    return data;
  } catch (error) {
    console.warn('[SessionManager] Error fetching chat history:', error);
    return { messages: [], totalCount: 0 };
  }
}

// Debug функция (доступна в window для отладки)
if (typeof window !== 'undefined') {
  window.debugSession = () => {
    console.log('=== Session Debug Info ===');
    const info = getSessionInfo();
    console.log('Session Info:', info);
    console.log('Visitor ID:', getCurrentVisitorId());
    console.log('Session Config:', SESSION_CONFIG);
  };
}

export default {
  getOrCreateSessionId,
  refreshSession,
  clearSession,
  getCurrentSessionId,
  getSessionInfo,
  isSessionExpired,
  getOrCreateBrowsingSessionId,
  getOrCreateVisitorId,
  getCurrentVisitorId,
  fetchChatHistory,
  SESSION_CONFIG,
};
